import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, FileVideo, Building2, LogOut, ShieldCheck, Store,
  Layers, Monitor, Store as StoreIcon, UserCircle, Image as ImageIcon, ListMusic, Calendar,
  Wallet, Menu, PanelLeftClose, PanelLeftOpen, ArrowLeft, Home,
  Globe2, BookOpen, Megaphone, BarChart3, UploadCloud, Inbox,
} from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/admin/dealers", label: "Dealers", Icon: Building2 },
  { to: "/admin/plans", label: "Plans", Icon: Layers },
  { to: "/admin/templates", label: "Templates", Icon: FileVideo },
  { to: "/admin/devices", label: "Devices", Icon: Monitor },
  { to: "/admin/app-updates", label: "App Updates", Icon: UploadCloud },
  { to: "/admin/payments", label: "Payments", Icon: Wallet },
  { to: "/admin/billing/salon-otp", label: "Billing", Icon: Wallet },
  { to: "/admin/clients", label: "All Clients", Icon: Users },
  { to: "/admin/marketing", label: "Marketing", Icon: Megaphone },
  { to: "/admin/website-enquiries", label: "Website Enquiries", Icon: Inbox, badgeKey: "website_enquiries_new" },
  { to: "/admin/website-analytics", label: "Website Analytics", Icon: BarChart3 },
  { to: "/admin/website", label: "Website CMS", Icon: Globe2 },
  { to: "/admin/e-catalogue", label: "E-Catalogue", Icon: BookOpen },
  { to: "/admin/seo", label: "SEO Settings", Icon: Globe2 },
];

const DEALER_NAV = [
  { to: "/dealer", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/dealer/clients", label: "My Clients", Icon: Users },
  { to: "/dealer/plans", label: "Plans", Icon: Layers },
  { to: "/dealer/screens", label: "Screens", Icon: Monitor },
  { to: "/dealer/templates", label: "Templates", Icon: FileVideo },
  { to: "/dealer/payments", label: "Payments", Icon: Wallet },
];

const CLIENT_NAV = [
  { to: "/client", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/client/devices", label: "Devices", Icon: Monitor },
  { to: "/client/media", label: "Media", Icon: ImageIcon },
  { to: "/client/playlists", label: "Playlists", Icon: ListMusic },
  { to: "/client/schedules", label: "Schedule", Icon: Calendar },
  { to: "/client/templates", label: "Templates", Icon: FileVideo },
  { to: "/client/payments", label: "Payments", Icon: Wallet },
  { to: "/client/storefront", label: "Store Front", Icon: StoreIcon },
  { to: "/client/society", label: "Society", Icon: Building2, vertical: "society" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const focusedBasicOrders = user?.role === "client" && location.pathname === "/client/storefront" && new URLSearchParams(location.search).get("tab") === "basic-orders";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => localStorage.getItem("sidebar_hidden") === "true");
  const [impersonatedByAdmin, setImpersonatedByAdmin] = useState(null);
  const [navBadges, setNavBadges] = useState({});
  const clientVertical = String(user?.vertical || "").toLowerCase();
  const visibleClientNav = CLIENT_NAV.filter((item) => !item.vertical || item.vertical === clientVertical);
  const items = user?.role === "admin" ? ADMIN_NAV : user?.role === "dealer" ? DEALER_NAV : visibleClientNav;
  const mobileItems = user?.role === "client"
    ? items.filter((item) => ["/client", "/client/storefront", "/client/society", "/client/media", "/client/playlists", "/client/payments"].includes(item.to))
    : user?.role === "dealer"
      ? items.filter((item) => ["/dealer", "/dealer/clients", "/dealer/screens", "/dealer/payments", "/dealer/templates"].includes(item.to))
      : items.filter((item) => ["/admin", "/admin/dealers", "/admin/marketing", "/admin/website-analytics", "/admin/devices", "/admin/app-updates", "/admin/payments", "/admin/billing/salon-otp", "/admin/clients"].includes(item.to));
  const RoleIcon = user?.role === "admin" ? ShieldCheck : user?.role === "dealer" ? Store : UserCircle;

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("sidebar_hidden", sidebarHidden ? "true" : "false");
  }, [sidebarHidden]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("impersonated_by_admin") || localStorage.getItem("impersonated_by_admin");
      setImpersonatedByAdmin(raw ? JSON.parse(raw) : null);
    } catch {
      setImpersonatedByAdmin(sessionStorage.getItem("impersonated_by_admin") ? {} : null);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let active = true;
    const loadBadges = async () => {
      try {
        const { data } = await api.get("/admin/website-enquiries", { params: { status: "New" } });
        if (active) setNavBadges({ website_enquiries_new: data?.counts?.new || 0 });
      } catch {
        if (active) setNavBadges({});
      }
    };
    loadBadges();
    const timer = window.setInterval(loadBadges, 60000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [user?.role, location.pathname]);

  const handleLogout = async () => {
    sessionStorage.removeItem("impersonated_by_admin");
    localStorage.removeItem("impersonated_by_admin");
    await logout();
    window.location.replace("https://rpsignage.com/");
  };
  const homePath = user?.role === "admin" ? "/admin" : user?.role === "dealer" ? "/dealer" : "/client";
  const currentItem = items.find((item) => {
    if (item.end) return location.pathname === item.to;
    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  }) || items[0];
  const showBack = location.pathname !== homePath;
  const goBack = () => {
    if (window.history.length > 1) nav(-1);
    else nav(homePath);
  };

  return (
    <div className="app-shell-root min-h-screen bg-white text-[#111827] overflow-x-hidden">
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {sidebarHidden && (
        <button
          type="button"
          onClick={() => setSidebarHidden(false)}
          className="fixed left-4 top-4 z-40 hidden h-10 w-10 items-center justify-center rounded-sm border border-[#E5E7EB] bg-white text-[#111827] shadow-sm hover:bg-[#F3F4F6] lg:inline-flex"
          aria-label="Show navigation"
          title="Show navigation"
        >
          <PanelLeftOpen className="h-5 w-5" />
        </button>
      )}

      <aside className={`app-shell-sidebar fixed inset-y-0 left-0 z-50 flex h-screen w-[84vw] max-w-xs flex-col border-r border-[#E5E7EB] bg-white transition-transform duration-200 lg:w-64 lg:flex ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} ${sidebarHidden ? "lg:-translate-x-full" : "lg:translate-x-0"}`}>
        <div className="px-6 py-6 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-[#111827] bg-black p-1">
              <img src="/logo.png" alt="RP Signage logo" className="h-full w-full object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-extrabold text-base tracking-tight leading-none">RP Signage</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-1">Control Panel</div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarHidden(true)}
              className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827] lg:inline-flex"
              aria-label="Hide navigation"
              title="Hide navigation"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map(({ to, label, Icon, end, badgeKey }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#111827] text-white hover:text-white"
                    : "text-[#374151] hover:bg-[#F3F4F6]"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span className="min-w-0 flex-1 truncate">{label}</span>
              {badgeKey && navBadges[badgeKey] > 0 && (
                <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-black text-white">{navBadges[badgeKey]}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#E5E7EB] px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#F3F4F6] flex items-center justify-center rounded-sm">
              <RoleIcon className="w-4 h-4 text-[#111827]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] truncate">{user?.role}</div>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-sm border-[#E5E7EB] hover:bg-[#F3F4F6]"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className={`app-shell-main min-w-0 transition-[padding] duration-200 ${sidebarHidden ? "lg:pl-0" : "lg:pl-64"}`}>
        {!focusedBasicOrders && <div className="app-shell-mobile-header sticky top-0 z-30 border-b border-[#E5E7EB] bg-white/95 px-3 py-2 backdrop-blur lg:hidden">
          <div className="flex w-full min-w-0 items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <button
                type="button"
                onClick={showBack ? goBack : () => nav(homePath)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-[#E5E7EB] text-[#111827]"
                aria-label={showBack ? "Go back" : "Go home"}
              >
                {showBack ? <ArrowLeft className="h-5 w-5" /> : <Home className="h-5 w-5" />}
              </button>
              <div className="min-w-0">
                <div className="truncate text-sm font-extrabold text-[#111827]">{currentItem?.label || "Dashboard"}</div>
                <div className="truncate text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">{user?.role || "control"}</div>
              </div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-[#E5E7EB] text-red-600"
                aria-label="Logout"
                title="Logout"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-[#E5E7EB] text-[#111827]"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>}
        <div className={`app-shell-content mx-auto w-full max-w-[1400px] min-w-0 sm:p-6 sm:pb-28 lg:p-8 ${focusedBasicOrders ? "p-1 pb-2" : "p-3 pb-24"}`}>
          {impersonatedByAdmin && (
            <div className={`app-shell-banner mb-4 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 ${focusedBasicOrders ? "hidden sm:block" : ""}`}>
              Viewing this panel from admin switch mode. Logout to end this session.
            </div>
          )}
          <Outlet />
        </div>
        {!focusedBasicOrders && <nav className="app-shell-mobile-nav fixed inset-x-0 bottom-0 z-40 border-t border-[#E5E7EB] bg-white/95 px-1 py-1 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
          <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.min(mobileItems.length, 5)}, minmax(0, 1fr))` }}>
            {mobileItems.slice(0, 5).map(({ to, label, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex min-h-14 flex-col items-center justify-center gap-1 rounded-sm px-1 text-[10px] font-semibold ${
                    isActive ? "bg-[#111827] text-white" : "text-[#4B5563]"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span className="max-w-full truncate">{label.replace("All ", "")}</span>
              </NavLink>
            ))}
          </div>
        </nav>}
      </main>
    </div>
  );
}
